import {
 Link, useLocation
} from 'react-router-dom'
import {
 clsx
} from 'clsx'
import {
 useAuth
} from '../hooks/useAuth'
import {
 useOrganizationAccess
} from '../hooks/useOrganizationAccess'
import {
 useState
} from 'react'
import {
 UserCircle
} from 'lucide-react'
import {
 useFeatureFlagEnabled
} from 'posthog-js/react'
import {
 FeatureFlags
} from '../analytics/posthog'
import {
 VIEWS_ROUTE
} from '../utils/routes'
import {
 ProfileMenu
} from './ProfileMenu'

function getNavLinkClasses(isActive: boolean): string {
  return clsx(
    'px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200',
    isActive
      ? 'text-primary-600 bg-primary-50'
      : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
  )
}

export function Navbar(): JSX.Element {
  const { user, isAuthenticated, logout, logoutEverywhere } = useAuth()
  const { enabled: teamEnabled } = useOrganizationAccess()
  const pagesEnabled = useFeatureFlagEnabled(FeatureFlags.ASSISTANT_VIEWS) === true
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleLogout = (): void => {
    void logout()
    setMobileMenuOpen(false)
  }

  return (
    <nav className="sticky top-0 z-50 bg-white shadow-soft border-b border-gray-200">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-1.5">
            <img src="/images/logo/symbol.svg" alt="" className="h-8 w-auto" />
            <img src="/images/logo/wordmark.svg" alt="Catch" className="h-7 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <Link
                  to="/stats"
                  className={getNavLinkClasses(location.pathname === '/stats')}
                >
                  Stats
                </Link>
                {pagesEnabled && (
                  <Link
                    to={VIEWS_ROUTE}
                    className={getNavLinkClasses(location.pathname.startsWith(VIEWS_ROUTE))}
                  >
                    Pages
                  </Link>
                )}
                {teamEnabled && (
                  <Link
                    to="/team"
                    className={getNavLinkClasses(location.pathname === '/team')}
                  >
                    Team
                  </Link>
                )}
                <Link
                  to="/integrations"
                  className={getNavLinkClasses(location.pathname === '/integrations')}
                >
                  Settings
                </Link>
                <div className="ml-2 pl-4 border-l border-gray-200">
                  <ProfileMenu
                    name={user?.name}
                    picture={user?.picture}
                    onLogout={handleLogout}
                    onLogoutEverywhere={() => { void logoutEverywhere() }}
                  />
                </div>
              </>
            ) : (
              <Link
                to="/login"
                className="bg-gradient-to-r from-primary-600 to-primary-500 text-white px-6 py-2 rounded-lg text-sm font-medium hover:from-primary-700 hover:to-primary-600 transition-[background-color,box-shadow,transform] duration-200 shadow-soft hover:shadow-soft-lg active:scale-[0.96]"
              >
                Get Started
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          {isAuthenticated && (
            <div className="md:hidden flex items-center space-x-2">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt="Profile"
                  className="w-8 h-8 rounded-full ring-2 ring-primary-100"
                />
              ) : (
                <UserCircle className="w-8 h-8 text-gray-400" />
              )}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-gray-600 hover:text-primary-600 hover:bg-primary-50 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {mobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          )}

          {/* Mobile "Get Started" button */}
          {!isAuthenticated && (
            <Link
              to="/login"
              className="md:hidden bg-gradient-to-r from-primary-600 to-primary-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-primary-700 hover:to-primary-600 transition-[background-color,box-shadow,transform] duration-200 shadow-soft active:scale-[0.96]"
            >
              Get Started
            </Link>
          )}
        </div>

        {/* Mobile Navigation Menu */}
        {isAuthenticated && mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2">
              <Link
                to="/stats"
                onClick={() => setMobileMenuOpen(false)}
                className={getNavLinkClasses(location.pathname === '/stats')}
              >
                Stats
              </Link>
              {pagesEnabled && (
                <Link
                  to={VIEWS_ROUTE}
                  onClick={() => setMobileMenuOpen(false)}
                  className={getNavLinkClasses(location.pathname.startsWith(VIEWS_ROUTE))}
                >
                  Pages
                </Link>
              )}
              {teamEnabled && (
                <Link
                  to="/team"
                  onClick={() => setMobileMenuOpen(false)}
                  className={getNavLinkClasses(location.pathname === '/team')}
                >
                  Team
                </Link>
              )}
              <Link
                to="/integrations"
                onClick={() => setMobileMenuOpen(false)}
                className={getNavLinkClasses(location.pathname === '/integrations')}
              >
                Settings
              </Link>
              <div className="pt-2 border-t border-gray-200 mt-2">
                <div className="px-3 py-2 text-sm text-gray-700">{user?.name}</div>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                >
                  Logout
                </button>
                <button type="button" onClick={() => { void logoutEverywhere() }} title="Use after changing or resetting the password you use to sign in to Catch" className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:text-primary-600">
                  Sign out on all devices
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
